import { useEffect, useState, useRef } from "react";
import { reduxForm, InjectedFormProps } from "redux-form";
import { Button, Form, Modal, Message } from "rsuite";
import avatar2 from "../../assets/icons/avatar2.jpeg";
import VsIcon from "shared/icons/VsIcon";
import AddResultForm from "components/AddReultForm";
import { BracketTeam, DisputedBracket, Seed } from "core/models/Bracket";
import { Tournament } from "core/models/Tournament";
import OpenEye from "shared/icons/OpenEye";

interface ResolveDisputeProps {
  onClose(): void;
  show: boolean;
  initialValues?: any;
  onSubmit(values: any): void;
  bracketTeams: BracketTeam[];
  tournament: Tournament;
  seed: DisputedBracket;
  isEdit?: boolean;
  handleOpenImage: (image: string) => void;
  handleConfirmBracketResult: () => void;
}

const handleSubmitFail = () => {
  setTimeout(() => {
    const firstElement = document.getElementsByClassName(
      "rs-error-message-wrapper"
    )[0];
    if (firstElement) {
      firstElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 50);
};

type FinalProps = InjectedFormProps<{}, ResolveDisputeProps> &
  ResolveDisputeProps;

function ResolveDispute({
  onClose,
  show,
  handleSubmit,
  initialValues,
  onSubmit,
  initialize,
  submitFailed,
  invalid,
  bracketTeams,
  tournament,
  seed,
  handleOpenImage,
  handleConfirmBracketResult,
}: FinalProps) {
  const [formError, setFormError] = useState(false);
  const defaultValues = useRef<any>({});
  const bestOfNumber = Number(seed?.matchFormat?.replace(/\D/g, ""));

  const MATCH_NO_OPTIONS: any = [];

  useEffect(() => {
    if (initialValues) {
      defaultValues.current = initialValues;
      initialize(initialValues);
    }
  }, [initialValues, initialize]);

  useEffect(() => {
    if (submitFailed) {
      setFormError(invalid);
    }
  }, [submitFailed, invalid]);

  const handleFormSubmit = (values: any) => {
    onSubmit(values);
  };

  useEffect(() => {
    if (!bracketTeams || !tournament?.tournamentConfig || !seed) return;

    const teams = bracketTeams;
    const config = tournament.tournamentConfig;

    const createPlayerResults = (
      teams: BracketTeam[],
      matchResult?: DisputedBracket
    ) =>
      teams.flatMap((team) =>
        team.user_teams.map((user) => {
          const stats: Record<string, any> = {};

          config.forEach((cfg) => {
            const existingStat = matchResult?.playerResults?.find(
              (p: any) => p.userId === user.userId
            )?.stats?.[cfg.key];

            stats[cfg.key] = existingStat ?? "";
          });

          return {
            userId: user.userId,
            userName: user?.inGameName || user.userName,
            teamId: team.id,
            stats,
          };
        })
      );

    const createTeamResults = (
      teams: BracketTeam[],
      matchResult?: DisputedBracket
    ) =>
      teams.map((team) => {
        const oldScore = matchResult?.teams?.find(
          (r: any) => r.id === team.id
        )?.score;

        return {
          teamId: team.id,
          score: oldScore ?? "",
        };
      });

    const results = createTeamResults(teams, seed);
    const playerResults = createPlayerResults(teams, seed);

    initialize({
      results,
      playerResults,
    });
  }, [bracketTeams, tournament, seed]);

  return (
    <Modal backdrop="static" onHide={onClose} show={show} size="md">
      <Modal.Header>
        <Modal.Title>View Dispute</Modal.Title>
      </Modal.Header>

      {formError && (
        <Message
          type="error"
          description="Fill all required fields before submitting"
        />
      )}
      <div className="match-detail-wrapper">
        <div className="match-round-details">
          <div className="match-name">Match {seed?.matchSequence}</div>
          <div className="dot"></div>
          <div className="round-name">{seed?.roundName}</div>
          <div className="dot"></div>
          <div className="round-name">Best of {bestOfNumber} </div>
        </div>

        <div className="match-view-detail-wrapper">
          <div className="team-details-section">
            <div className="team-details-section">
              {bracketTeams?.map((team, index) => {
                const seedTeam = seed?.teams?.find(
                  (t: any) => t.id === team.id
                );

                return (
                  <>
                    <div className="team-detail">
                      <div className="team-detail-left">
                        <div className="team-img">
                          <img
                            src={seedTeam?.image || avatar2}
                            alt="team"
                            className="team-img"
                          />
                        </div>
                        <div className="team-details-name">
                          <div className="team-name">{seedTeam?.name}</div>
                          <div className="team-ingame-name">
                            SEED #{index + 1}
                          </div>
                          <div
                            className={`team-check-in ${
                              seedTeam?.checkedIn
                                ? "checked-in"
                                : "not-checked-in"
                            }`}
                          >
                            {seedTeam?.checkedIn
                              ? "Checked In"
                              : "Not Checked In"}
                          </div>
                        </div>
                      </div>

                      <div className="team-detail-right">
                        <div className="score">{seedTeam?.score ?? "-"}</div>
                      </div>
                    </div>

                    {index === 0 && (
                      <div className="vs-icon">
                        <VsIcon />
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Form id="resolveForm" fluid onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="form-container">
          <Modal.Body>
            <AddResultForm
              bracketTeams={bracketTeams}
              tournamentConfig={tournament?.tournamentConfig}
              bestOfNumber={bestOfNumber}
              isRoundRobin={false}
              matchOptions={MATCH_NO_OPTIONS}
            />
            <div className="upload-images">
              <div className="qualifer-grid-form-wrapper">
                <div className="qualifer-grid-form-100">
                  <div className="image-uploaded">
                    <div className="image-preview-content">
                      <div className="image-preview">
                        <img
                          src={seed?.dispute.screenshots[0]}
                          alt="uploaded"
                          className="image-preview"
                        />
                      </div>
                    </div>

                    <div
                      className="image-actions"
                      onClick={() => {
                        handleOpenImage(seed.dispute?.screenshots[0]);
                      }}
                    >
                      <OpenEye color="#389cff" />
                    </div>
                  </div>
                </div>
                <div className="qualifer-grid-form-100">
                  <div className="image-uploaded">
                    <div className="image-preview-content">
                      <div className="image-preview">
                        <img
                          src={seed.screenshots[0]}
                          alt="uploaded"
                          className="image-preview"
                        />
                      </div>
                    </div>

                    <div
                      className="image-actions"
                      onClick={() => {
                        handleOpenImage(seed.screenshots[0]);
                      }}
                    >
                      <OpenEye color="#389cff" />
                    </div>
                  </div>
                </div>

                <div className="qualifer-grid-form-100"></div>
              </div>
            </div>
          </Modal.Body>
        </div>
        <div className="reason-container">
          <div className="reason-header">
            Dispute Reason from{" "}
            {
              bracketTeams?.find((team) => team.id === seed?.approvalTeamId)
                ?.name
            }
          </div>{" "}
          <div className="reason-text">{seed?.dispute?.reason}</div>
        </div>

        <Modal.Footer>
          <div className="review-footer-btn">
            <Button appearance="ghost" type="submit" form="resolveForm" block>
              Update Result
            </Button>

            <Button
              appearance="primary"
              type="button"
              block
              onClick={handleConfirmBracketResult}
            >
              Confirm Result
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default reduxForm<{}, ResolveDisputeProps>({
  form: "dispute-resolve",
  destroyOnUnmount: true,
  onSubmitFail: handleSubmitFail,
})(ResolveDispute);
